import { Box, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useState } from 'react';
import formatMoney from '../../core/formatMoney';
import assert from '../../core/util/assert';
import CleaningForm from './CleaningForm';

type CleaningStatus = 'paid' | 'unpaid' | 'missing';

const getStatusColors = (status: CleaningStatus) => {
    switch (status) {
        case 'paid':
            return {
                background: 'rgba(34, 197, 94, 0.3)',
                border: '#22c55e',
                text: '#bbf7d0',
            };
        case 'unpaid':
            return {
                background: 'rgba(245, 158, 11, 0.3)',
                border: '#f59e0b',
                text: '#fde68a',
            };
        case 'missing':
            return {
                background: 'rgba(245, 158, 11, 0.15)',
                border: '#f59e0b',
                text: '#fde68a',
            };
    }
};

const CleaningCard = ({
    cleaning,
    suggestedTime,
}: {
    suggestedTime?: string;
    cleaning?: {
        id: string;
        time: string;
        note: string;
        value: number;
        paid: boolean;
    };
}) => {
    const [formOpen, setFormOpen] = useState(false);
    const cleaningTimeString = cleaning?.time ?? suggestedTime;
    assert(cleaningTimeString);
    const cleaningTime = DateTime.fromSQL(cleaningTimeString);

    const getStatus = (): CleaningStatus => {
        if (!cleaning) {
            return 'missing';
        }
        if (cleaning.paid) {
            return 'paid';
        }
        return 'unpaid';
    };

    const status = getStatus();
    const colors = getStatusColors(status);
    const borderStyle = status === 'missing' ? 'dashed' : 'solid';

    return (
        <Box
            onClick={() => setFormOpen(true)}
            sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 2,
                alignItems: 'center',
                mb: 2,
                p: 2,
                backgroundColor: colors.background,
                border: `2px ${borderStyle} ${colors.border}`,
                borderRadius: 0,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                '&:hover': {
                    backgroundColor: colors.border,
                    transform: 'translateX(2px)',
                },
            }}
        >
            <Typography
                sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: colors.text,
                    fontSize: '0.875rem',
                }}
            >
                {cleaningTime.toFormat('cccc HH:mm, LLL dd')}
            </Typography>
            {cleaning && (
                <Typography
                    sx={{
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: colors.text,
                        fontSize: '0.875rem',
                        textAlign: 'right',
                    }}
                >
                    {formatMoney(-cleaning.value, 'EUR')}
                </Typography>
            )}
            {formOpen && (
                <CleaningForm
                    suggestedTime={suggestedTime}
                    cleaning={cleaning}
                    onClose={() => {
                        setFormOpen(false);
                    }}
                />
            )}
        </Box>
    );
};

export default CleaningCard;
